<template>
<div>
  <div class="p-2 row g-4">
    <div class="col-sm-6 col-xl-3">
        <SummaryBox type="currency" iconType="fa-solid fa-chart-line" title="Total Investment" :amount=bondSummary.total />
    </div>
    <div class="col-sm-6 col-xl-3">
        <SummaryBox type="percent" iconType="fa-solid fa-chart-column" title="AVG Interest" :amount=bondSummary.avgInterest />
    </div>

    <div class="col-sm-6 col-xl-3">
        <SummaryBox type="currency" iconType="fa-solid fa-sack-dollar" title="Total Interest" :amount="bondSummary.totalInterestPaid" />
    </div>
  </div>

  <div class="p-2">
    <v-card flat rounded="lg" class="bond-list-card">
      <v-card-title class="d-flex align-center justify-space-between px-4 py-3">
        <span class="text-h6 font-weight-bold">Bonds</span>

        <v-btn color="primary" variant="tonal" rounded="lg" @click="openAddDialog">
          <font-awesome-icon icon="plus" class="mr-2" />Add Bond
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-2" />

      <v-data-table
        v-else
        :headers="headers"
        :items="bonds"
        item-value="bond_id"
        class="custom-data-table"
        :loading="loading"
        no-data-text="No bonds available."
      >

        <template v-slot:[`item.actions`]="{ item }">
          <font-awesome-icon icon="edit" class="mr-2 cursor-pointer" @click="editItem(item)" />
          <font-awesome-icon icon="trash" class="cursor-pointer" @click="deleteItem(item)" />
        </template>
      </v-data-table>

      <v-dialog v-model="dialog" max-width="600px">
        <v-card>
          <v-card-title>
            <span class="headline">{{ editedIndex.value === -1 ? 'Add' : 'Edit' }} Bond</span>
          </v-card-title>

          <v-card-text>
            <v-container>
              <v-row>
                <v-col cols="12" sm="6" v-for="field in bondFields" :key="field.model">
                  <v-text-field
                    v-if="!field.select"
                    v-model="editedItem[field.model]"
                    :label="field.label"
                    :type="field.type || 'text'"
                    variant="outlined"
                    :disabled="field.readonly || false"
                  />

                  <v-select
                    v-else-if="field.select"
                    v-model="editedItem[field.model]"
                    :items="field.items"
                    :label="field.label"
                    variant="outlined"
                  />
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>

          <v-card-actions>
            <v-spacer />
            <v-btn variant="text" @click="closeDialog">Cancel</v-btn>
            <v-btn color="primary" variant="tonal" @click="save">Save</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>
  </div>
</div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useQuery, useMutation } from '@vue/apollo-composable'
import { GET_BONDS, ADD_BOND, UPDATE_BOND, DELETE_BOND } from '@/graphql/bonds'
import SummaryBox from '@/components/Summary/Box.ce.vue';

const bonds = ref([])
const dialog = ref(false)
const menuPurchase = ref(false)
const menuMaturity = ref(false)
const menuCouponDate = ref(false);
const editedIndex = ref(-1)
const editedItem = ref({})
const loading = ref(false)

// Table Headers
const headers = [
  { title: 'Bond ID', key: 'bond_id' },
  { title: 'Name', key: 'name' },
  { title: 'Code', key: 'code' },
  { title: 'Invest Amount', key: 'invest_amount' },
  { title: 'Interest (%)', key: 'interest' },
  { title: 'Interest Amount', key: 'interest_amount' },
  { title: 'Total Interest Paid', key: 'total_interest_paid' },
  { title: 'coupon Date', key: 'coupon_date' },
  { title: 'Payment Type', key: 'payment_type' },
  { title: 'Purchase Type', key: 'purchase_type' },
  { title: 'Remaining Term', key: 'remaining_term', sortable: true, customKeySort: (a, b) => a.remainingDays - b.remainingDays },
  { title: 'Turn Your', key: 'turn_your', sortable: true, customKeySort: (a, b) => a.turnYourDays - b.turnYourDays },
  { title: 'Purchase Date', key: 'purchase_date' },
  { title: 'Maturity Date', key: 'maturity_date' },
  { title: 'Actions', key: 'actions', sortable: false }
]

// Bond Form Fields
const bondFields = [
  { label: 'Bond ID', model: 'bondId', readonly: true },
  { label: 'name', model: 'name' },
  { label: 'Code', model: 'code' },
  { label: 'Invest Amount', model: 'investAmount', type: 'number' },
  { label: 'Interest (%)', model: 'interest', type: 'number' },
  { label: 'Interest Amount', model: 'interest_amount', type: 'number' },
  { label: 'Coupon Date', model: 'coupon_date', type: 'date', menu: menuCouponDate },
  { label: 'Payment Type', model: 'paymentType', select: true, items: ['Month', 'Quarter', 'Year', 'Cumulative'] },
  { label: 'Purchase Type', model: 'purchaseType', select: true, items: ['IPO', 'Market'] },
  { label: 'Purchase Date', model: 'purchaseDate', type: 'date', menu: menuPurchase },
  { label: 'Maturity Date', model: 'maturityDate', type: 'date', menu: menuMaturity }
]

// Apollo Queries and Mutations
const { refetch } = useQuery(GET_BONDS)
const { mutate: addBond } = useMutation(ADD_BOND)
const { mutate: updateBond } = useMutation(UPDATE_BOND)
const { mutate: deleteBond } = useMutation(DELETE_BOND)

onMounted(() => {
  fetchBonds()
})

function getDurationData(purchaseDate, maturityDate) {
  const start = new Date(purchaseDate)
  const end = new Date(maturityDate)
  const now = new Date()

  const totalDays = Math.floor((end - start) / (1000 * 60 * 60 * 24))
  const remainingDays = Math.floor((end - now) / (1000 * 60 * 60 * 24))

  // Full duration formatting
  let years = end.getFullYear() - start.getFullYear()
  let months = end.getMonth() - start.getMonth()
  let days = end.getDate() - start.getDate()

  if (days < 0) {
    months -= 1
    const previousMonth = new Date(end.getFullYear(), end.getMonth(), 0)
    days += previousMonth.getDate()
  }
  if (months < 0) {
    years -= 1
    months += 12
  }

  let fullDuration = ''
  if (years > 0) fullDuration += `${years} year `
  if (months > 0) fullDuration += `${months} mo `
  if (days > 0 || (!years && !months)) fullDuration += `${days} days`

  // Remaining duration formatting
  const remYears = Math.floor(remainingDays / 365)
  const remMonths = Math.floor((remainingDays % 365) / 30)
  const remDays = remainingDays % 30

  let remainingDuration = ''
  if (remainingDays < 0) {
    remainingDuration = 'Matured'
  } else {
    if (remYears > 0) remainingDuration += `${remYears} yr `
    if (remMonths > 0) remainingDuration += `${remMonths} mo `
    if (remDays > 0 || (!remYears && !remMonths)) remainingDuration += `${remDays} d`
  }

  return {
    turnYour: fullDuration.trim(),
    turnYourDays: totalDays,
    remainingTerm: remainingDuration.trim(),
    remainingDays,
  }
}

// Methods
function fetchBonds() {
  loading.value = true
  refetch().then(({ data }) => {
    bonds.value = (data?.bonds || []).map(b => {
      const duration = getDurationData(b.purchase_date, b.maturity_date)
      return {
        ...b,
        investAmount: b.invest_amount,
        interestAmount: b.interest_amount,
        purchaseDate: b.purchase_date,
        maturityDate: b.maturity_date,
        bondId: b.bond_id,
        turn_your: duration.turnYour,
        turnYourDays: duration.turnYourDays,
        remaining_term: duration.remainingTerm,
        remainingDays: duration.remainingDays,
      }
    }) 
  }).finally(() => {
    loading.value = false
  })
}

function openAddDialog() {
  editedIndex.value = -1
  editedItem.value = {
    bond_id: 0,
    name: '',
    code: '',
    invest_amount: 0,
    interest: 0,
    interest_amount: 0,
    coupon_date: '',
    payment_type: '',
    purchase_type: '',
    purchase_date: '',
    maturity_date: ''
  }
  dialog.value = true
}

function editItem(item) {
  editedIndex.value = bonds.value.indexOf(item)
  editedItem.value = { ...item }
  dialog.value = true
}

function save() {
  loading.value = true

  // Prepare the payload with correct variable names
  const payload = {
    name            : editedItem.value.name || '',
    code            : editedItem.value.code || '',
    invest_amount   : parseFloat(editedItem.value.investAmount || 0),
    interest        : parseFloat(editedItem.value.interest || 0),
    interest_amount : parseFloat(editedItem.value.interest_amount || 0),
    coupon_date     : editedItem.value.coupon_date || '',
    payment_type    : editedItem.value.paymentType || '',
    purchase_type   : editedItem.value.purchaseType || '',
    purchase_date   : editedItem.value.purchaseDate || '',
    maturity_date   : editedItem.value.maturityDate || '',
  }

  // Use the mutation with correct input data
  if (editedIndex.value === -1) {
    addBond({ input: payload })
      .then(fetchBonds)
      .catch(error => {
        console.log(error.message)
      })
  } else {
    updateBond({ bond_id: editedItem.value.bondId, input: payload }).then(fetchBonds)
  }

  closeDialog();
}

function deleteItem(item) {
  loading.value = true
  deleteBond({ bond_id: item.bond_id }).then(fetchBonds)
}

function closeDialog() {
  dialog.value = false
  editedItem.value = {} // Reset form after close
}

// Computed
const bondSummary = computed(() => {
  if (!bonds.value.length) return { total: 0, avgInterest: 0, avgDuration: 0 }

  const total = bonds.value.reduce((sum, bond) => sum + Number(bond.investAmount || 0), 0)
  const avgInterest = bonds.value.reduce((sum, bond) => sum + Number(bond.interest || 0), 0) / bonds.value.length
  const avgDuration = bonds.value.reduce((sum, bond) => sum + Number(bond.turnYourDays || 0), 0) / bonds.value.length
  const totalInterestPaid = bonds.value.reduce((sum, bond) => sum + Number(bond.total_interest_paid || 0), 0)

  return {
    total,
    avgInterest: avgInterest.toFixed(2),
    avgDuration: Math.round(avgDuration),
    totalInterestPaid: totalInterestPaid.toFixed(2),
  }
})
</script>

<style scoped>
.bond-list-card {
  border: 1px solid var(--border);
}

.custom-data-table :deep(thead th) {
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-muted);
  background-color: #fafafa;
  border-bottom: 1px solid var(--border) !important;
}

.custom-data-table :deep(tbody tr) {
  transition: background-color 0.15s ease;
}

.custom-data-table :deep(tbody tr:hover) {
  background-color: #f8f9fb;
}

.custom-data-table :deep(tbody td) {
  font-size: 0.875rem;
  border-bottom: 1px solid #f1f2f4 !important;
}
</style>